// verify — run a yaku's example hand through the engine and report whether the target yaku CAN fire
// on it, plus the best-variant score for display. Used by YakuToggle's "Verify" button (spec §6.1).
//
// Subtlety: a single example hand may decompose several ways and may fire a SUPERIOR yaku that
// subsumes the target (e.g. an all-concealed-triplets toitoi example is also suuankou on tsumo, and
// detectYaku returns yakuman-only). So Verify proves "the target appears in SOME decomposition under
// SOME reasonable win context" — it scans both ron/tsumo and every decomposition + every winning
// tile, then reports the matching variant. This makes Verify a faithful "does this example trigger
// this yaku?" check rather than a "is it the single best result?" check.
import type { RuleSet, TileId, WinContext, YakuDefMeta } from '@mj/protocol';
import type { EngineClient } from './engineClient';
import { buildWinContext } from './store';

export interface VerifyResult {
  fired: { id: string; han: number }[];
  yakuman: { id: string; mult: number }[];
  han: number;
  fu: number;
  points: number;
  error?: string;
  /** True if the verified yaku appears in some decomposition under some reasonable win context. */
  targetFired: boolean;
}

// Context flavours to try. Some yaku only fire under specific contexts (riichi/haitei/firstTurn);
// we set those when the id demands it, and otherwise try plain ron + plain tsumo.
function ctxVariants(def: YakuDefMeta): WinContext[] {
  const base = {
    menzen: true as boolean,
    roundWind: 'z1' as TileId,
    seatWind: (def.id === 'yakuhai-seat' ? 'z2' : 'z1') as TileId,
  };
  const id = def.id;
  const special: Partial<Record<string, Partial<Parameters<typeof buildWinContext>[0]>>> = {
    'riichi': { riichi: true },
    'ippatsu': { riichi: true, ippatsu: true },
    'double-riichi': { riichi: true, doubleRiichi: true, firstTurn: true },
    'open-riichi': { riichi: true, openRiichi: true },
    'open-double-riichi': { riichi: true, doubleRiichi: true, openRiichi: true, firstTurn: true },
    'haitei': { lastTile: 'haitei' },
    'houtei': { lastTile: 'houtei' },
    'rinshan': { afterKan: true },
    'chankan': { afterKan: true },
    'tenhou': { isDealer: true, firstTurn: true },
    'chiihou': { firstTurn: true },
    'renhou': { firstTurn: true },
    'juusanbuttou': { firstTurn: true },
    'hachirenchan': { dealerWinStreak: 8, isDealer: true },
  };
  const extra = special[id] ?? {};
  // Try tsumo and ron; for triplet-only yaku ron avoids suuankou subsumption of the completed set.
  const tsumo = buildWinContext({ winBy: 'tsumo', ...base, ...extra });
  const ron = buildWinContext({ winBy: 'ron', ...base, ...extra });
  // houtei/chankan/renhou are ron-only contexts.
  if (id === 'houtei' || id === 'chankan' || id === 'renhou') return [ron];
  if (id === 'haitei' || id === 'rinshan' || id === 'tenhou' || id === 'chiihou' || id === 'juusanbuttou') return [tsumo];
  return [ron, tsumo];
}

export async function verifyYaku(
  client: EngineClient,
  def: YakuDefMeta,
  rs: RuleSet,
  catalog: YakuDefMeta[],
): Promise<VerifyResult> {
  const hand = def.exampleHand;
  if (hand.length < 13) {
    return { fired: [], yakuman: [], han: 0, fu: 0, points: 0, targetFired: false, error: 'example hand too short' };
  }
  // Candidate winning tiles: any distinct tile present (the win can complete any group/pair/wait).
  const winCandidates = Array.from(new Set(hand)) as TileId[];

  let display: VerifyResult | null = null;
  let targetFired = false;

  for (const ctx of ctxVariants(def)) {
    for (const winTile of winCandidates) {
      const parses = await client.call('parseAgari', { concealed: hand, melds: [], winTile, winBy: ctx.winBy });
      if (parses.length === 0) continue;

      // Per-decomposition detection: does the target appear in ANY decomposition?
      for (const ph of parses) {
        const yr = await client.call('detectYaku', { ph, ctx, rs, catalog, tiles: hand });
        if (yr.yaku.some((y) => y.id === def.id) || yr.yakuman.some((y) => y.id === def.id)) {
          targetFired = true;
        }
      }

      // Best-variant score for the display line (first valid winTile/ctx that scores).
      if (!display) {
        const scored = await client.call('score', { parses, ctx, rs, catalog, tiles: hand, opts: { players: rs.players } });
        const yrBest = await client.call('detectYaku', { ph: parses[0]!, ctx, rs, catalog, tiles: hand });
        if (!('error' in scored)) {
          display = {
            fired: scored.yaku.map((y) => ({ id: String(y.id), han: y.han })),
            yakuman: yrBest.yakuman.map((y) => ({ id: y.id, mult: y.mult })),
            han: scored.han,
            fu: scored.fu,
            points: scored.points,
            targetFired: false,
          };
        }
      }
      if (targetFired && display) break;
    }
    if (targetFired && display) break;
  }

  if (!display) {
    return { fired: [], yakuman: [], han: 0, fu: 0, points: 0, targetFired, error: 'no scorable decomposition' };
  }
  return { ...display, targetFired };
}
